import React, { useEffect } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Badge,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import {
  Message as MessageIcon,
  FolderShared as FolderSharedIcon,
} from "@mui/icons-material";

import { ClientMessages } from "../ClientSummaryComponentModules";
import SharedFiles from "../../../../../components/organisms/SharedFiles";
import NewColors from "../../../../../themes/NewColors";

const ClientFloatingActions = ({ messageGroup, client }) => {
  const [messagesOpen, setMessagesOpen] = React.useState(false);
  const [filesOpen, setFilesOpen] = React.useState(false);

  // Effect to disable background scrolling when dialog is open
  useEffect(() => {
    const handleBodyScroll = (disable) => {
      if (disable) {
        // Save current scroll position
        const scrollY = window.scrollY;

        // Apply styles to disable scrolling while preserving scroll position
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";
        document.body.style.overflowY = "scroll";
      } else {
        // Get the scroll position from the body's top property
        const scrollY = document.body.style.top;

        // Restore normal scrolling
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflowY = "";

        // Restore scroll position
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    };

    // Disable scrolling if either dialog is open
    if (messagesOpen || filesOpen) {
      handleBodyScroll(true);
    } else {
      handleBodyScroll(false);
    }

    // Clean up by restoring scrolling when component unmounts
    return () => {
      handleBodyScroll(false);
    };
  }, [messagesOpen, filesOpen]);

  return (
    <>
      {/* Floating Action Buttons */}
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Tooltip title="Open Messages">
          <IconButton
            color="primary"
            size="large"
            sx={{
              bgcolor: "primary.main",
              color: "white",
              "&:hover": { bgcolor: "primary.dark" },
            }}
            onClick={() => setMessagesOpen(!messagesOpen)}
          >
            <Badge badgeContent={0} color="error">
              <MessageIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title="Open Shared Files">
          <IconButton
            color="secondary"
            size="large"
            sx={{
              bgcolor: "secondary.main",
              color: "white",
              "&:hover": { bgcolor: "secondary.dark" },
            }}
            onClick={() => setFilesOpen(!filesOpen)}
          >
            <FolderSharedIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Messages Panel */}
      {messagesOpen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 100,
            right: 20,
            width: 500,
            height: 650, // Increased from 540 to 650 to make it taller
            zIndex: 1000,
            boxShadow: 3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: NewColors.cardBackground,
            }}
          >
            <CardHeader
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                py: 1,
                backgroundColor: "primary.main",
                color: "white",
              }}
              title="Messages"
              action={
                <IconButton
                  onClick={() => setMessagesOpen(false)}
                  sx={{ color: "white" }}
                >
                  ×
                </IconButton>
              }
            />
            <CardContent
              sx={{
                flex: 1,
                p: 0,
                overflow: "hidden", // Changed from "auto" to "hidden" to prevent nested scrollbars
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ClientMessages uid={client?.uid} groupId={messageGroup?.id} clientAvatarUrl={client?.avatarUrl} />
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Shared Files Panel */}
      {filesOpen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 100,
            right: 20,
            width: 500,
            height: 650, // Also increased here for consistency
            zIndex: 1000,
            boxShadow: 3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: NewColors.cardBackground,
            }}
          >
            <CardHeader
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                py: 1,
                backgroundColor: "secondary.main",
                color: "white",
              }}
              title="Shared Files"
              action={
                <IconButton
                  onClick={() => setFilesOpen(false)}
                  sx={{ color: "white" }}
                >
                  ×
                </IconButton>
              }
            />
            <CardContent
              sx={{
                flex: 1,
                p: 0,
                overflow: "auto", // Changed to auto to enable scrolling
                display: "flex",
                flexDirection: "column",
              }}
            >
              <SharedFiles uid={client.uid} />
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Optional semi-transparent overlay to block background interaction */}
      {(messagesOpen || filesOpen) && (
        <Box
          onClick={() => {
            if (messagesOpen) setMessagesOpen(false);
            if (filesOpen) setFilesOpen(false);
          }}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.3)",
            zIndex: 999, // Just below the dialog
            cursor: "pointer",
          }}
        />
      )}
    </>
  );
};

export default ClientFloatingActions;
